// @ts-nocheck
import { Link } from 'react-router-dom';
import { Container, Stack, Grid, Group, Center } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Title, Text, Badge, Button } from '@/components/ui/core';
import {
  IconLogin,
  IconUserPlus,
  IconKey,
  IconLockCheck,
  IconDeviceMobile,
  IconDots,
  IconExternalLink,
  IconEye
} from '@tabler/icons-react';

interface AuthCategory {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  count: number;
  color: string;
  variants: string[];
}

const categories: AuthCategory[] = [
  {
    title: 'Login Pages',
    description: 'User authentication entry points with various design styles',
    icon: <IconLogin size={32} />,
    path: '/app/demo/auth-pages/login',
    count: 17,
    color: 'indigo',
    variants: ['Classic Minimal', 'Hero-Image', 'Split-Screen', 'Centered Card', 'Mobile App Style', 'Dark Mode', 'Social Login', 'Glassmorphism', 'Gradient', 'Sidebar', 'Carousel', 'Enterprise Split', 'Modern', 'Animated', 'Illustrated', 'Parallax', 'Minimal 3D'],
  },
  {
    title: 'Registration Pages',
    description: 'New user sign-up pages with validation and multi-step options',
    icon: <IconUserPlus size={32} />,
    path: '/app/demo/auth-pages/register',
    count: 17,
    color: 'cyan',
    variants: ['Classic Minimal', 'Hero-Image', 'Split-Screen', 'Centered Card', 'Mobile App Style', 'Dark Mode', 'Social Login', 'Glassmorphism', 'Gradient', 'Sidebar', 'Carousel', 'Enterprise Split', 'Modern', 'Animated', 'Illustrated', 'Parallax', 'Minimal 3D'],
  },
  {
    title: 'Forgot Password',
    description: 'Password recovery initiation pages',
    icon: <IconKey size={32} />,
    path: '/app/demo/auth-pages/forgot-password',
    count: 17,
    color: 'orange',
    variants: ['Classic Minimal', 'Hero-Image', 'Split-Screen', 'Centered Card', 'Mobile App Style', 'Dark Mode', 'Social Login', 'Glassmorphism', 'Gradient', 'Sidebar', 'Carousel', 'Enterprise Split', 'Modern', 'Animated', 'Illustrated', 'Parallax', 'Minimal 3D'],
  },
  {
    title: 'Reset Password',
    description: 'Password reset forms with strength indicators',
    icon: <IconLockCheck size={32} />,
    path: '/app/demo/auth-pages/reset-password',
    count: 17,
    color: 'green',
    variants: ['Classic Minimal', 'Hero-Image', 'Split-Screen', 'Centered Card', 'Mobile App Style', 'Dark Mode', 'Social Login', 'Glassmorphism', 'Gradient', 'Sidebar', 'Carousel', 'Enterprise Split', 'Modern', 'Animated', 'Illustrated', 'Parallax', 'Minimal 3D'],
  },
  {
    title: 'OTP / 2FA',
    description: 'Two-factor authentication and verification code pages',
    icon: <IconDeviceMobile size={32} />,
    path: '/app/demo/auth-pages/otp',
    count: 17,
    color: 'violet',
    variants: ['Classic Minimal', 'Hero-Image', 'Split-Screen', 'Centered Card', 'Mobile App Style', 'Dark Mode', 'Social Login', 'Glassmorphism', 'Gradient', 'Sidebar', 'Carousel', 'Enterprise Split', 'Modern', 'Animated', 'Illustrated', 'Parallax', 'Minimal 3D'],
  },
  {
    title: 'Other Auth Pages',
    description: 'Additional authentication related pages',
    icon: <IconDots size={32} />,
    path: '/app/demo/auth-pages/other',
    count: 5,
    color: 'pink',
    variants: ['Change Password', 'Email Verification', 'Account Locked', '2FA Setup', 'Logout'],
  },
];

export default function AuthPagesOverview() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={1}>Auth Pages Demo</Title>
              <Text c="dimmed" size="lg" mt="xs">
                A comprehensive collection of authentication page designs with 17 unique variants for each category.
              </Text>
            </div>
            <Badge size="lg" variant="gradient" gradient={{ from: 'indigo', to: 'cyan' }}>
              85+ Templates
            </Badge>
          </Group>
        </Stack>

        {/* Design Variants Legend */}
        <Card variant="subtle" p="md">
          <Stack gap="sm">
            <Title order={4}>Design Variants</Title>
            <Group gap="lg" wrap="wrap">
              <Group gap="xs">
                <Badge color="gray" variant="dot">Classic Minimal</Badge>
                <Text size="xs" c="dimmed">Clean, centered cards</Text>
              </Group>
              <Group gap="xs">
                <Badge color="orange" variant="dot">Hero-Image</Badge>
                <Text size="xs" c="dimmed">Full background images</Text>
              </Group>
              <Group gap="xs">
                <Badge color="lime" variant="dot">Split-Screen</Badge>
                <Text size="xs" c="dimmed">50/50 content layouts</Text>
              </Group>
              <Group gap="xs">
                <Badge color="blue" variant="dot">Centered Card</Badge>
                <Text size="xs" c="dimmed">Elevated with strong shadows</Text>
              </Group>
              <Group gap="xs">
                <Badge color="teal" variant="dot">Mobile App Style</Badge>
                <Text size="xs" c="dimmed">Rounded inputs, mobile-first</Text>
              </Group>
              <Group gap="xs">
                <Badge color="dark" variant="dot">Dark Mode</Badge>
                <Text size="xs" c="dimmed">High-contrast dark theme</Text>
              </Group>
              <Group gap="xs">
                <Badge color="cyan" variant="dot">Social Login</Badge>
                <Text size="xs" c="dimmed">Social providers first</Text>
              </Group>
              <Group gap="xs">
                <Badge color="violet" variant="dot">Glassmorphism</Badge>
                <Text size="xs" c="dimmed">Frosted glass effect</Text>
              </Group>
              <Group gap="xs">
                <Badge color="grape" variant="dot">Gradient</Badge>
                <Text size="xs" c="dimmed">Gradient backgrounds</Text>
              </Group>
              <Group gap="xs">
                <Badge color="indigo" variant="dot">Sidebar</Badge>
                <Text size="xs" c="dimmed">Left sidebar layout</Text>
              </Group>
              <Group gap="xs">
                <Badge color="pink" variant="dot">Carousel</Badge>
                <Text size="xs" c="dimmed">Auto-rotating slides</Text>
              </Group>
              <Group gap="xs">
                <Badge color="red" variant="dot">Enterprise Split</Badge>
                <Text size="xs" c="dimmed">Features panel layout</Text>
              </Group>
              <Group gap="xs">
                <Badge color="teal" variant="dot">Modern</Badge>
                <Text size="xs" c="dimmed">Floating elements & patterns</Text>
              </Group>
              <Group gap="xs">
                <Badge color="pink" variant="dot">Animated</Badge>
                <Text size="xs" c="dimmed">Staggered motion effects</Text>
              </Group>
              <Group gap="xs">
                <Badge color="violet" variant="dot">Illustrated</Badge>
                <Text size="xs" c="dimmed">SVG art & stock photos</Text>
              </Group>
              <Group gap="xs">
                <Badge color="indigo" variant="dot">Parallax</Badge>
                <Text size="xs" c="dimmed">Depth & mouse movement</Text>
              </Group>
              <Group gap="xs">
                <Badge color="dark" variant="dot">Minimal 3D</Badge>
                <Text size="xs" c="dimmed">Floating cards with tilt</Text>
              </Group>
            </Group>
          </Stack>
        </Card>

        {/* Categories Grid */}
        <Grid gutter="lg">
          {categories.map((category) => (
            <Grid.Col key={category.title} span={{ base: 12, sm: 6, lg: 4 }}>
              <Card
                variant="elevated"
                p="lg"
                h="100%"
                style={{
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                }}
                component={Link}
                to={category.path}
              >
                <Stack gap="md" h="100%">
                  <Group justify="space-between">
                    <Center
                      style={(theme) => ({
                        width: 56,
                        height: 56,
                        borderRadius: theme.radius.md,
                        backgroundColor: `var(--mantine-color-${category.color}-light)`,
                        color: `var(--mantine-color-${category.color}-filled)`,
                      })}
                    >
                      {category.icon}
                    </Center>
                    <Badge color={category.color} variant="light" size="lg">
                      {category.count} variants
                    </Badge>
                  </Group>

                  <div style={{ flex: 1 }}>
                    <Title order={3} mb="xs">{category.title}</Title>
                    <Text size="sm" c="dimmed" lineClamp={2}>
                      {category.description}
                    </Text>
                  </div>

                  <Group gap="xs" wrap="wrap">
                    {category.variants.map((variant) => (
                      <Badge key={variant} size="sm" variant="outline" color="gray">
                        {variant}
                      </Badge>
                    ))}
                  </Group>

                  <Button
                    variant="light"
                    color={category.color}
                    fullWidth
                    rightSection={<IconEye size={16} />}
                  >
                    View All Variants
                  </Button>
                </Stack>
              </Card>
            </Grid.Col>
          ))}
        </Grid>

        {/* Features Section */}
        <Card variant="subtle" p="lg" mt="md">
          <Grid gutter="xl">
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="xs" align="center" ta="center">
                <Title order={2} c="indigo">85+</Title>
                <Text fw={500}>Auth Page Templates</Text>
                <Text size="sm" c="dimmed">Ready to use in your projects</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="xs" align="center" ta="center">
                <Title order={2} c="cyan">17</Title>
                <Text fw={500}>Design Variants</Text>
                <Text size="sm" c="dimmed">Each category with unique styles</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="xs" align="center" ta="center">
                <Title order={2} c="green">100%</Title>
                <Text fw={500}>Responsive</Text>
                <Text size="sm" c="dimmed">Works on all screen sizes</Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>
      </Stack>
    </Container>
  );
}
